import React, { useState, useRef, useEffect } from 'react'

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'most_comments', label: 'Most Comments' },
  { value: 'most_reactions', label: 'Most Reactions' },
]

function ActionStrip({
  searchTerm,
  onSearchChange,
  selectedPeopleCount = 0,
  onFilterClick,
  sortBy,
  onSortChange,
}) {
  const [sortOpen, setSortOpen] = useState(false)
  const sortRef = useRef(null)

  const isFilterActive = selectedPeopleCount > 0
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Newest First'

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="sticky top-0 z-10 bg-fb-cream py-3">
      <div className="flex items-center gap-3">
        {/* Search input */}
        <div className="flex-1 relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search stories..."
            className="w-full pl-10 pr-9 py-2.5 rounded-lg border border-gray-300 bg-white font-serif text-base focus:outline-none focus:ring-2 focus:ring-fb-olive focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter by Person button */}
        <button
          onClick={onFilterClick}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-lg font-serif text-sm font-semibold transition-colors whitespace-nowrap
            ${isFilterActive
              ? 'bg-fb-olive text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filter by Person
          {isFilterActive && (
            <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {selectedPeopleCount}
            </span>
          )}
        </button>

        {/* Sort dropdown */}
        <div className="relative" ref={sortRef}>
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-gray-300 font-serif text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            </svg>
            {currentSortLabel}
            <svg className={`w-4 h-4 transition-transform ${sortOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {sortOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              {SORT_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    onSortChange(option.value)
                    setSortOpen(false)
                  }}
                  className={`w-full text-left px-4 py-2 font-serif text-sm flex items-center gap-2 hover:bg-gray-50 ${
                    sortBy === option.value ? 'text-fb-olive font-semibold' : 'text-gray-700'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    sortBy === option.value ? 'border-fb-olive' : 'border-gray-300'
                  }`}>
                    {sortBy === option.value && (
                      <span className="w-2 h-2 rounded-full bg-fb-olive" />
                    )}
                  </span>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ActionStrip
